import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Autocomplete, Chip, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createMold, updateMold, getMold, listEquipment, type MoldListItem, type SaveMoldPayload } from '../../api/echipamente'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  compatibleEquipmentIds: z.array(z.string()),
  compatibleArticleIds: z.array(z.string()),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', compatibleEquipmentIds: [], compatibleArticleIds: [] }

export default function MoldFormDialog({ mold, onClose }: { mold: MoldListItem | null | 'new'; onClose: () => void }) {
  const open = mold !== null
  const editing = mold !== null && mold !== 'new'
  const qc = useQueryClient()

  const { data: equipment } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment, enabled: open })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['mentenanta', 'matrita', editing ? (mold as MoldListItem).id : null],
    queryFn: () => getMold((mold as MoldListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, name: detail.name,
        compatibleEquipmentIds: detail.compatibleEquipmentIds, compatibleArticleIds: detail.compatibleArticleIds,
      })
    } else if (mold === 'new') {
      reset(EMPTY)
    }
  }, [mold, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveMoldPayload = { ...values }
      if (editing) return updateMold((mold as MoldListItem).id, payload)
      return createMold(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mentenanta', 'matrite'] })
      onClose()
    },
  })

  const findEquipment = (id: string) => equipment?.find((e) => e.id === id)
  const findArticle = (id: string) => articles?.find((a) => a.id === id)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare matriță' : 'Matriță nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
              )} />
            </Stack>
            <Controller name="compatibleEquipmentIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={equipment?.map((e) => e.id) ?? []}
                getOptionLabel={(id) => findEquipment(id)?.name ?? id}
                value={field.value} onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findEquipment(id)?.name ?? id} size="small" />
                  })}
                renderInput={(params) => <TextField {...params} label="Utilaje compatibile" />}
              />
            )} />
            <Controller name="compatibleArticleIds" control={control} render={({ field }) => (
              <Autocomplete
                multiple options={articles?.map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = findArticle(id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value} onChange={(_, v) => field.onChange(v)}
                renderValue={(value, getItemProps) =>
                  value.map((id, index) => {
                    const { key, ...itemProps } = getItemProps({ index })
                    return <Chip key={key} {...itemProps} label={findArticle(id)?.code ?? id} size="small" />
                  })}
                renderInput={(params) => <TextField {...params} label="Articole formate" />}
              />
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
